/**
 * Small presentation helpers shared by the editor's components.
 *
 * They live here rather than beside the components that use them because a module that exports
 * both a component and a plain function loses fast refresh for the whole file.
 */
import type { AdminTourPlatformKey, AdminTourStep } from '../../../types/adminTours';

export const platformTabId = (idPrefix: string, platform: AdminTourPlatformKey) =>
  `${idPrefix}-tab-${platform}`;

export const platformPanelId = (idPrefix: string, platform: AdminTourPlatformKey) =>
  `${idPrefix}-panel-${platform}`;

/**
 * Which part of which step a server field error belongs to.
 *
 * Two shapes, because a draft carries two kinds of step. A published step is named by its key
 * (`steps.<step key>.<titleOverride|bodyOverride>`); a step the admin has just added has no key
 * yet, so the API names it by its position in the `newSteps` array it was sent in
 * (`newSteps[0].title`).
 *
 * Parsed rather than substring-matched: a step key can contain a dot (`calls.page.one`), and it can
 * contain the words "title" or "body", both of which send a substring match to the wrong field.
 */
export type StepFieldPart = 'title' | 'body' | null;

export type StepFieldError =
  | { kind: 'step'; stepKey: string; part: StepFieldPart }
  | { kind: 'newStep'; index: number; part: StepFieldPart };

const partOf = (segment: string): StepFieldPart => {
  const last = segment.toLowerCase();
  return last.startsWith('title') ? 'title' : last.startsWith('body') ? 'body' : null;
};

export const parseStepFieldError = (field: string): StepFieldError | null => {
  const newStep = /^newSteps\[(\d+)\]\.(.+)$/.exec(field);
  if (newStep) {
    return { kind: 'newStep', index: Number(newStep[1]), part: partOf(newStep[2]) };
  }

  const parts = field.split('.');
  if (parts.length < 3 || parts[0] !== 'steps') return null;
  return {
    kind: 'step',
    stepKey: parts.slice(1, -1).join('.'),
    part: partOf(parts[parts.length - 1]),
  };
};

/** What the anchor line says, which is three different facts rather than one optional string. */
export const anchorSummary = (step: AdminTourStep): string => {
  if (step.anchorId === null) return 'No highlight';
  if (step.anchorLabel === null) return 'Anchor missing from the app';
  return step.anchorLabel;
};
